import { useEffect, useState } from 'react'
import { Bell, BellOff, BellRing, Share, SquarePlus } from 'lucide-react'
import { showToast } from './Toast'
import { useI18n } from '../i18n/useI18n'
import { canPromptInstall, promptInstall } from '../lib/installPrompt'
import {
  ensurePushSubscription,
  isStandalone,
  needsA2hsForPush,
  pushSupported,
  subscribeToPush,
} from '../lib/webPush'

/**
 * iOS でホーム画面に追加する 3 ステップ。`NotificationPermissionPrompt` (注文履歴の
 * シート) と `PushSetupCard` の両方で出すので 1 箇所に置く。
 */
export function A2hsSteps() {
  const { t } = useI18n()
  return (
    <ol className="mt-2 space-y-1.5 font-body text-xs text-text-secondary">
      <li className="flex items-center gap-2">
        <Share size={14} className="shrink-0 text-navy" />
        <span>1. {t('push.installStep1')}</span>
      </li>
      <li className="flex items-center gap-2">
        <SquarePlus size={14} className="shrink-0 text-navy" />
        <span>2. {t('push.installStep2')}</span>
      </li>
      <li className="flex items-center gap-2">
        <Bell size={14} className="shrink-0 text-navy" />
        <span>3. {t('push.installStep3')}</span>
      </li>
    </ol>
  )
}

/**
 * #1990 通知を受け取る準備 (ホーム画面追加 / 通知の許可) への常設の入口。
 *
 * #1230 の `NotificationPermissionPrompt` は注文履歴でだけ出る下からのシートで、
 * 一度閉じると二度と出ない。予約とお知らせで通知の恩恵を受ける人 (日本人の
 * リピーター) は注文履歴を開かない限りその案内に出会わなかった。
 *
 * 置く場所は 2 つ:
 * - `reservation`: 予約完了の直後。「承認されたらお知らせします」と**具体的な次の
 *   イベント**を言えるので、追加する理由がその場で成立する (#1989 の push が前提)
 * - `profile`: プロフィールに常設。閉じた人が戻れる場所。状態 (オン / オフ / 拒否) を出す
 *
 * 状態は端末側の事実 (Notification.permission と standalone 起動) から出発し、
 * 許可済みなら購読が本当に確立できるかまで確かめて「オン」と言う — 許可だけ見て
 * 「オンです」と出すと、VAPID 未設定 (local) やサーバ不通のときに届かないのに
 * オンと表示してしまう。
 */
type Mode =
  | 'install'      // iOS のブラウザ表示: ホーム画面に追加しないと通知 API 自体が無い
  | 'default'      // 通知は使えるがまだ許可を聞いていない
  | 'granted'      // 許可済みで購読も確立できた (= 届く)
  | 'failed'       // 許可はあるが購読の確立に失敗した (サーバ不通・SW 未登録など)。再試行できる
  | 'denied'       // 一度拒否された。ブラウザ設定から戻すしかない
  | 'unsupported'  // 通知に対応していないブラウザ

function detectMode(): Mode {
  if (needsA2hsForPush()) return 'install'
  if (!pushSupported()) return 'unsupported'
  return Notification.permission === 'granted'
    ? 'granted'
    : Notification.permission === 'denied' ? 'denied' : 'default'
}

export default function PushSetupCard({ context }: { context: 'reservation' | 'profile' }) {
  const { t, locale } = useI18n()
  const [mode, setMode] = useState<Mode>(() => detectMode())
  const [busy, setBusy] = useState(false)

  // 予約完了では「これから何かできる人」にだけ出す。既に届く人・使えない人には
  // 何も見せない (完了画面を長くしない)
  const isReservation = context === 'reservation'
  const hidden = isReservation && mode !== 'install' && mode !== 'default'

  // 許可済みなら購読を確かめる。起動時にも App 側 (useOrderReadyNotifier) が同じ
  // 呼び出しをしているが、ここでは**結果を表示に反映する**のが目的 — 失敗したら
  // 「オンです」ではなく再試行を出す。冪等なので二重に呼んでも購読は増えない
  useEffect(() => {
    if (hidden || mode !== 'granted') return
    let cancelled = false
    void ensurePushSubscription(locale).then((ok) => {
      if (!cancelled && !ok) setMode('failed')
    })
    return () => { cancelled = true }
  }, [hidden, mode, locale])

  if (hidden) return null

  const enable = async () => {
    if (busy) return
    setBusy(true)
    try {
      const result = await subscribeToPush(locale)
      if (result === 'ok') {
        setMode('granted')
        return
      }
      // 許可は取れたが購読に失敗 → 再試行できる状態に。拒否はブラウザ設定でしか戻せない
      setMode(result === 'denied' ? 'denied' : Notification.permission === 'granted' ? 'failed' : mode)
      // 黙って何も起きないボタンにしない。理由を出す (AnnouncementOptIn と同じ)
      showToast({
        type: 'error',
        message: t(result === 'denied' ? 'announcement.optInDenied' : 'announcement.optInUnavailable'),
      })
    } finally {
      setBusy(false)
    }
  }

  const installNow = async () => {
    // Android: beforeinstallprompt が取れていればネイティブのインストールダイアログ。
    // 通知はブラウザのままでも届くので、こちらは任意の導線
    await promptInstall()
  }

  const Icon = mode === 'granted' ? BellRing : mode === 'denied' ? BellOff : Bell
  const tone = mode === 'granted' ? 'matcha' : mode === 'denied' || mode === 'unsupported' ? 'gray' : 'gold'
  const body = {
    install: t(isReservation ? 'push.reservationInstallBody' : 'push.settingsInstall'),
    default: t(isReservation ? 'push.reservationBody' : 'push.settingsOff'),
    granted: t('push.settingsOn'),
    failed: t('push.settingsFailed'),
    denied: t('push.settingsDenied'),
    unsupported: t('push.settingsUnsupported'),
  }[mode]

  return (
    <div
      className={`rounded-2xl border bg-white p-4 shadow-card ${
        tone === 'gold' ? 'border-gold/40' : 'border-gray-100'
      }`}
      data-push-mode={mode}
    >
      <div className="flex items-start gap-3">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
            tone === 'matcha' ? 'bg-matcha/15' : tone === 'gold' ? 'bg-gold/15' : 'bg-gray-100'
          }`}
        >
          <Icon
            size={17}
            className={tone === 'matcha' ? 'text-matcha' : tone === 'gold' ? 'text-gold' : 'text-text-tertiary'}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-body text-sm font-semibold text-text-primary">
            {t(isReservation ? 'push.reservationTitle' : 'push.settingsTitle')}
          </p>
          <p className="mt-0.5 font-body text-xs leading-snug text-text-secondary">{body}</p>
          {/* 何が届くかを 1 行。「通知」だけでは許可する理由が伝わらない。
            * 旅先のお知らせは別の同意 (AnnouncementOptIn) なのでここには書かない */}
          {(mode === 'install' || mode === 'default' || mode === 'granted') && (
            <p className="mt-1 font-body text-[11px] leading-snug text-text-tertiary">
              {t('push.whatYouGet')}
            </p>
          )}

          {mode === 'install' && <A2hsSteps />}

          {(mode === 'default' || mode === 'failed') && (
            <div className="mt-2.5 flex gap-2">
              <button
                onClick={() => void enable()}
                disabled={busy}
                className="flex-1 rounded-lg bg-navy py-2 font-display text-sm font-semibold text-white tap-effect hover:bg-navy-light disabled:opacity-60"
              >
                {t(mode === 'failed' ? 'push.retry' : 'push.enable')}
              </button>
              {mode === 'default' && !isStandalone() && canPromptInstall() && (
                <button
                  onClick={() => void installNow()}
                  className="flex-1 rounded-lg border border-navy/30 bg-white py-2 font-display text-sm font-semibold text-navy tap-effect hover:bg-navy/5"
                >
                  {t('push.installButton')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
